import React from 'react';
import { digitsOnlyPercent, formatMoney, lineCharges } from '../../utils/money';
import { Spinner } from '../common/Feedback';
import { TrashIcon } from '../common/icons';

function PercentInput({ label, value, onChange }) {
  return (
    <label className="dp-pct">
      <span>{label}</span>
      <input
        inputMode="numeric"
        maxLength={3}
        placeholder="0"
        value={value ? String(value) : ''}
        onChange={(e) => onChange(Number(digitsOnlyPercent(e.target.value)) || 0)}
      />
      <em>%</em>
    </label>
  );
}

function CartLine({ line, editable, stylists, onChange, onRemove }) {
  const c = lineCharges(line);
  const set = (patch) => onChange({ ...line, ...patch });
  const needsPrice = line.price_missing && !(Number(line.unit_price) > 0);
  return (
    <div className={`dp-cart-line${needsPrice ? ' needs-price' : ''}`}>
      <div className="dp-cart-line-top">
        <div>
          <strong>{line.name}</strong>
          {line.requested_name && line.requested_name.toLowerCase() !== String(line.name).toLowerCase() ? (
            <small>Booked as “{line.requested_name}”</small>
          ) : null}
        </div>
        {editable ? (
          <button type="button" className="dp-icon-btn dp-icon-btn--danger" onClick={onRemove} aria-label={`Remove ${line.name}`}>
            <TrashIcon size={16} />
          </button>
        ) : null}
      </div>

      <div className="dp-cart-line-mid">
        {editable ? (
          <div className="dp-qty">
            <button type="button" onClick={() => set({ quantity: Math.max(1, (Number(line.quantity) || 1) - 1) })} aria-label="Less">
              −
            </button>
            <span>{line.quantity}</span>
            <button type="button" onClick={() => set({ quantity: (Number(line.quantity) || 1) + 1 })} aria-label="More">
              +
            </button>
          </div>
        ) : (
          <span className="dp-muted">Qty {line.quantity}</span>
        )}
        {editable && (line.price_missing || !(Number(line.unit_price) > 0)) ? (
          <label className="dp-price-input">
            ₹
            <input
              inputMode="decimal"
              placeholder="Price"
              value={line.unit_price ? String(line.unit_price) : ''}
              onChange={(e) => set({ unit_price: Number(e.target.value.replace(/[^\d.]/g, '')) || 0 })}
            />
          </label>
        ) : (
          <span className="dp-muted">{formatMoney(line.unit_price)} each</span>
        )}
        <strong className="dp-cart-amount">{formatMoney(c.net)}</strong>
      </div>
      {needsPrice ? <p className="dp-cart-warn">No price in this branch&apos;s list — type the price.</p> : null}

      {editable ? (
        <>
          <select className="dp-input dp-input--sm" value={line.stylist_name || ''} onChange={(e) => set({ stylist_name: e.target.value })}>
            <option value="">Stylist</option>
            {stylists.map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </select>
          <div className="dp-pct-row">
            <PercentInput label="Discount" value={line.discount_percent} onChange={(v) => set({ discount_percent: v })} />
            <PercentInput label="Voucher" value={line.voucher_percent} onChange={(v) => set({ voucher_percent: v })} />
          </div>
        </>
      ) : (
        <div className="dp-cart-meta">
          {line.stylist_name ? <span>Stylist: {line.stylist_name}</span> : null}
          {c.discountPct ? <span>Discount {c.discountPct}%</span> : null}
          {c.voucherPct ? <span>Voucher {c.voucherPct}%</span> : null}
        </div>
      )}
    </div>
  );
}

/**
 * Right-hand order panel: lines (editable on the services step), offers, totals and the step's main action.
 */
export default function CartPanel({
  lines,
  totals,
  editable,
  stylists,
  offers,
  onOffers,
  onLineChange,
  onLineRemove,
  cta,
  extraActions,
  hint,
  branchName,
}) {
  return (
    <aside className="dp-cart" aria-label="Order">
      <div className="dp-cart-head">
        <h3>Order</h3>
        {branchName ? <span className="dp-pill dp-pill--muted">{branchName}</span> : null}
      </div>
      <div className="dp-cart-lines">
        {lines.length ? (
          lines.map((line) => (
            <CartLine
              key={line.key}
              line={line}
              editable={editable}
              stylists={stylists}
              onChange={onLineChange}
              onRemove={() => onLineRemove(line.key)}
            />
          ))
        ) : (
          <p className="dp-muted dp-cart-empty">No services yet. Pick from the price list.</p>
        )}
      </div>

      <div className="dp-cart-foot">
        {editable ? (
          <div className="dp-pct-row dp-pct-row--total">
            <span>On total</span>
            <PercentInput label="Discount" value={offers.total_discount_percent} onChange={(v) => onOffers({ ...offers, total_discount_percent: v })} />
            <PercentInput label="Voucher" value={offers.total_voucher_percent} onChange={(v) => onOffers({ ...offers, total_voucher_percent: v })} />
          </div>
        ) : null}
        <div className="dp-total-row">
          <span>Services</span>
          <strong>{formatMoney(totals.gross)}</strong>
        </div>
        {totals.lineOffers + totals.billOffers > 0 ? (
          <div className="dp-total-row dp-total-row--off">
            <span>Discount & voucher</span>
            <strong>− {formatMoney(totals.lineOffers + totals.billOffers)}</strong>
          </div>
        ) : null}
        <div className="dp-total-row">
          <span>Tax ({Math.round((totals.taxRate || 0) * 100)}%)</span>
          <strong>{formatMoney(totals.tax)}</strong>
        </div>
        <div className="dp-total-row dp-total-row--grand">
          <span>Total</span>
          <strong>{formatMoney(totals.grand)}</strong>
        </div>
        {cta ? (
          <button type="button" className="dp-btn dp-btn--block dp-btn--lg" onClick={cta.onClick} disabled={cta.disabled || cta.busy}>
            {cta.busy ? <Spinner small /> : null}
            {cta.label}
          </button>
        ) : null}
        {extraActions || null}
        {hint ? <p className="dp-cart-hint">{hint}</p> : null}
      </div>
    </aside>
  );
}
